import dayjs, { Dayjs, ManipulateType } from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

export type GranularityUnit =
  | 'year'
  | 'month'
  | 'day'
  | 'hour'
  | 'minute'
  | 'second';

/**
 * Derive the rounding granularity from an ISO 8601 period string.
 *
 * The least-significant component that is explicitly written (even if its
 * value is 0) determines the granularity.  Examples:
 *   P1D      → 'day'
 *   P1Y2M    → 'month'  (month is more granular than year)
 *   PT1H     → 'hour'
 *   PT1H0M   → 'minute'  (0M is explicitly present)
 *   PT0H0M0S → 'second'
 *   P1DT6H   → 'hour'   (time section takes precedence over date section)
 *
 * Throws if `period` is not a valid ISO 8601 duration (fails isDuration check).
 */
export const parsePeriodGranularity = (period: string): GranularityUnit => {
  if (!isDuration(period)) {
    throw new Error(
      `parsePeriodGranularity: "${period}" is not a valid ISO 8601 period`,
    );
  }
  const trimmed = period.trim().replace(/^-/, '');
  const tIdx = trimmed.indexOf('T');
  const datePart = tIdx >= 0 ? trimmed.slice(1, tIdx) : trimmed.slice(1);
  const timePart = tIdx >= 0 ? trimmed.slice(tIdx + 1) : '';

  let unit: GranularityUnit = 'day'; // default for date-only periods

  if (/\d+Y/.test(datePart)) unit = 'year';
  if (/\d+M/.test(datePart)) unit = 'month';
  if (/\d+D/.test(datePart)) unit = 'day';

  if (timePart) {
    if (/\d+H/.test(timePart)) unit = 'hour';
    if (/\d+M/.test(timePart)) unit = 'minute';
    if (/\d+S/.test(timePart)) unit = 'second';
  }

  return unit;
};

/**
 * Round a date up to the next calendar boundary at the given granularity.
 * If the date is already exactly on a boundary it is returned unchanged.
 *
 * Boundaries are evaluated in the timezone of the dayjs object.  Pass a
 * UTC dayjs (dayjs.utc(...)) for UTC-aligned boundaries.
 *
 * Examples (UTC input):
 *   roundUpToGranularity(2025-04-24T10:35:00Z, 'day')    → 2025-04-25T00:00:00Z
 *   roundUpToGranularity(2025-04-24T10:35:00Z, 'hour')   → 2025-04-24T11:00:00Z
 *   roundUpToGranularity(2025-04-24T10:00:00Z, 'hour')   → 2025-04-24T10:00:00Z (on boundary)
 */
export const roundUpToGranularity = (
  date: Dayjs,
  unit: GranularityUnit,
): Dayjs => {
  const djUnit = unit as ManipulateType;
  const boundary = date.startOf(djUnit);
  if (boundary.isSame(date)) return date;
  return boundary.add(1, djUnit);
};

/**
 * Round a date down to the current calendar boundary at the given granularity
 * (i.e. truncate). Always returns a value ≤ the input.
 *
 * Boundaries are evaluated in the timezone of the dayjs object.  Pass a
 * UTC dayjs (dayjs.utc(...)) for UTC-aligned boundaries.
 *
 * Examples (UTC input):
 *   roundDownToGranularity(2025-04-24T10:35:00Z, 'day')  → 2025-04-24T00:00:00Z
 *   roundDownToGranularity(2025-04-24T10:35:00Z, 'hour') → 2025-04-24T10:00:00Z
 *   roundDownToGranularity(2025-04-24T10:00:00Z, 'hour') → 2025-04-24T10:00:00Z (on boundary)
 */
export const roundDownToGranularity = (
  date: Dayjs,
  unit: GranularityUnit,
): Dayjs => date.startOf(unit as ManipulateType);

/**
 * Check if a string is an ISO 8601 duration (starts with P or -P)
 */
export const isDuration = (value: string): boolean => {
  if (typeof value !== 'string') return false;
  return /^-?P/.test(value.trim());
};

/**
 * Parse an ISO 8601 duration string and return a dayjs duration object.
 * Supports negative durations prefixed with '-' (e.g., '-P1D' for 1 day ago)
 */
export const parseDuration = (
  value: string,
): { duration: duration.Duration; negative: boolean } => {
  const trimmed = value.trim();
  const negative = trimmed.startsWith('-');
  const durationStr = negative ? trimmed.slice(1) : trimmed;
  return {
    duration: dayjs.duration(durationStr),
    negative,
  };
};

/**
 * Round a dayjs time to the nearest interval (in milliseconds).
 * @param time - The time to round
 * @param intervalMs - The interval in milliseconds to round to
 * @param mode - 'floor' (default), 'ceil', or 'round'
 */
export const roundTime = (
  time: Dayjs,
  intervalMs: number,
  mode: 'floor' | 'ceil' | 'round' = 'floor',
): Dayjs => {
  if (!intervalMs || intervalMs <= 0) return time;

  const ms = time.valueOf();
  let rounded: number;

  switch (mode) {
    case 'ceil':
      rounded = Math.ceil(ms / intervalMs) * intervalMs;
      break;
    case 'round':
      rounded = Math.round(ms / intervalMs) * intervalMs;
      break;
    case 'floor':
    default:
      rounded = Math.floor(ms / intervalMs) * intervalMs;
      break;
  }

  return dayjs(rounded);
};

/**
 * Parse a time value that can be either:
 * - An ISO 8601 date-time string (e.g., '2025-01-01T00:00:00Z')
 * - An ISO 8601 duration relative to now (e.g., 'PT0H', '-P1D', 'P7D')
 *
 * When `granularityUnit` is supplied it takes precedence over `roundIntervalMs`
 * and applies calendar-aware rounding — only to duration-based values, not to
 * absolute date strings:
 *   - Positive duration → ceil  (roundUpToGranularity) — snap forward
 *   - Negative duration → floor (roundDownToGranularity) — snap backward
 *
 * @param value - The time string to parse
 * @param reference - Reference time for duration calculations (default: now)
 * @param roundIntervalMs - Optional ms interval for legacy floor-rounding
 * @param granularityUnit - Optional calendar unit for granularity rounding
 */
export const parseTimeValue = (
  value: string,
  reference: Dayjs = dayjs(),
  roundIntervalMs?: number,
  granularityUnit?: GranularityUnit,
): Dayjs => {
  let result: Dayjs;
  const isDur = isDuration(value);
  let negative = false;

  if (isDur) {
    const { duration: dur, negative: isNeg } = parseDuration(value);
    negative = isNeg;
    result = isNeg ? reference.subtract(dur) : reference.add(dur);
  } else {
    result = dayjs(value);
  }

  if (isDur && granularityUnit) {
    result = negative
      ? roundDownToGranularity(result, granularityUnit)
      : roundUpToGranularity(result, granularityUnit);
  } else if (roundIntervalMs && roundIntervalMs > 0) {
    result = roundTime(result, roundIntervalMs);
  }

  return result;
};

/**
 * Parse a time range where each value can be a date-time or duration.
 *
 * @param range - Array of two time strings [start, end]
 * @param reference - Reference time for duration calculations (default: now)
 * @param roundIntervalMs - Optional ms interval for legacy floor-rounding
 * @param granularityUnit - Optional calendar unit for ceil-rounding (takes precedence)
 */
export const parseTimeRange = (
  range: [string, string],
  reference: Dayjs = dayjs(),
  roundIntervalMs?: number,
  granularityUnit?: GranularityUnit,
): [Dayjs, Dayjs] => {
  return [
    parseTimeValue(range[0], reference, roundIntervalMs, granularityUnit),
    parseTimeValue(range[1], reference, roundIntervalMs, granularityUnit),
  ];
};

/**
 * Parse increment value which can be:
 * - A number (seconds)
 * - An ISO 8601 duration string (e.g., 'PT1H', 'P1D')
 *
 * @returns Interval in milliseconds
 */
export const parseIncrement = (increment: number | string): number => {
  if (typeof increment === 'number') {
    return increment * 1000; // Convert seconds to milliseconds
  }
  if (typeof increment === 'string' && isDuration(increment)) {
    const { duration: dur } = parseDuration(increment);
    return dur.asMilliseconds();
  }
  return 0;
};
